import React, { useState, useCallback, useContext, useMemo, useRef, useEffect } from 'react';
import { TextField, DefaultButton } from '@fluentui/react';
import PropTypes from 'prop-types';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { ReactTemplate } from '@bingads-webui-react/react-template';
import { LearnMoreLink } from '@bingads-webui-campaign-react/help';
import { notificationApi } from '@bingads-webui-react/notification-center';
import { withScope } from '@bingads-webui-react/scope-service';
import { withPermissions } from '@bingads-webui-react/hoc-with-permissions';
import _ from 'underscore';
import { v4 as generateGuid } from 'uuid';
import classNames from 'classnames';
import { getCurrencyOptions } from '@bingads-webui-campaign-react/conversion-utils';
import { LearnMore } from '../learn-more/learn-more';
import { getStepStyles } from '../shared-styles/shared-content-style';
import { Badge } from './badge';
import { ClarityEventSetupSvg } from './clarity-event-setup-svg';

const styles = (components, { palette = {} }) => ({
  eventTrackingContainer: {
    display: 'flex',
  },
  eventTrackingViewContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  websiteUrlText: {
    fontSize: '12px',
  },
  websiteURLTextField: {
    width: '418px',
  },
  redoTrackingViewContainer: {
    width: '418px',
  },
  badgesContainer: {
    display: 'flex',
    width: '235px',
    justifyContent: 'space-between',
    marginTop: '5px',
  },
  eventTrackingButton: {
    marginLeft: '5px',
  },
  skipForNowContainer: {
    alignSelf: 'center',
    marginLeft: '18px',
  },
  clarityEventSetup: {
    '& .btn-link': {
      color: palette.themePrimary,
    },
  },
});
const MESSAGES = {
  INIT: 'INIT_CLARITY_EVENT_SETUP',
  ACK: 'ACK_INIT_CLARITY_EVENT_SETUP',
  SAVE_EVENT: 'SAVE_EVENT_DEFINITION',
  ACK_SAVE_EVENT: 'ACK_SAVE_EVENT_DEFINITION',
  REINIT: 'REINIT_CLARITY_EVENT_SETUP',
  ACK_REINIT: 'ACK_REINIT_CLARITY_EVENT_SETUP',
  END_SESSION: 'END_CLARITY_EVENT_SETUP_SESSION',
};
const validateUrl = (url) => {
  if (!url || url.trim().length === 0) {
    return false;
  }
  let parsedUrl;
  try {
    parsedUrl = new URL(url);
  } catch (ex) {
    return false;
  }
  return parsedUrl.protocol.toLowerCase() === 'http:' || parsedUrl.protocol.toLowerCase() === 'https:';
};
export const STAGES = {
  NOTCONNECTED: 0,
  STARTINIT: 1,
  WAITINGFORACK: 2,
  CONNECTED: 3,
  SYNCINGEVENTS: 4,
  TRACKINGEVENT: 5,
};
const validateProp = (props, dependency, expectedProperties) => {
  const dep = props[dependency];
  expectedProperties.forEach((key) => {
    if (!dep[key]) {
      throw new Error(`Expected parameter ${key} is not present in dependency ${dependency}`);
    }
  });
};

const getUrlObjectWithDebugParameter = (url) => {
  const urlObj = new URL(url);
  urlObj.searchParams.set('bat_debug', new Date().getTime());
  return urlObj;
};

const UnstyledClarityEventSetup = (props) => {
  const {
    classes, serverContext, scope, clarityTagSetupRepository: {
      getClarityEventInfo, getErrorMessage, saveGoal, processPayload, postClarityEventInfo,
    }, wizardContext,
    setClarityEventObjectExists, setShouldBlockerLoading, goToManualSetupPage, goalAction,
  } = props;

  const [currentStage, setCurrentStage] = useState(STAGES.NOTCONNECTED);

  // Validate shared prop between ESC and Expert mode here.
  validateProp(props, 'clarityTagSetupRepository', ['getClarityEventInfo', 'getErrorMessage', 'saveGoal', 'processPayload', 'postClarityEventInfo']);
  validateProp(props, 'wizardContext', ['cid', 'aid', 'goBack', 'goToNext', 'cancel', 'goalContext', 'permissions', 'logActivity']);

  const i18n = useContext(I18nContext);
  const useMockListener = scope && scope.values && scope.values.useMockListener;
  const [notificationError, setNotificationError] = useState(null);
  const [websiteUrl, setWebsiteUrl] = useState(null);
  const [errorMsg, setErrorMsg] = useState(null);
  // need ref to read the state value in add event listener callback (need to check if there is a cleaner solution)
  const currentTimer = useRef(-1);
  const closeTimer = useRef(-1);
  const connectionTimeout = useRef(-1);
  const currentAbortController = useRef(null);
  const sessionId = useRef('');

  const [currentTab, setCurrentTab] = useState(null);
  const { goalContext, logActivity } = wizardContext;
  const tagName = _.find(goalContext.tags, item => item.Id === goalContext.Tag.Id).Name;
  const tagId = goalContext.Tag.Id.toString();
  const receivedACK = useRef(false);
  const isRetrievingClarityEvent = useRef(false);
  const clarityEventInfoId = useRef(null);

  // single object since we should have one event per goal
  const [pageEvent, setPageEvent] = useState(null);
  const { Token } = serverContext;
  const messageParams = {
    TagId: tagId,
    Goal: {
      Name: goalContext.Name,
      Type: goalContext.Type,
      ValueType: goalContext.GoalValueType,
      Operator: goalContext.Operator,
      UrlString: goalContext.UrlString,
    },
    CurrencyCode: goalContext.CurrencyCode,
    CurrencyOptions: getCurrencyOptions(i18n),
    aid: wizardContext.aid.toString(),
    cid: wizardContext.cid.toString(),
    PageTrackingId: serverContext.PageTrackingId.toString(),
    UserId: serverContext.CurrentUser.Id.toString(),
  };
  const tagNameModel = { tagName: () => (<b>{tagName}</b>), bold: text => (<b>{text}</b>) };

  const errorMessages = useMemo(() => ({
    InvalidUrl: i18n.getString(_TL_('Please input a valid URL starting with http:// or https://')),
    NewTabError: i18n.getString(_TL_('Could not open new page.')),
    CannotDetectTagError: (
      <p>
        <ReactTemplate
          model={tagNameModel}
          template={i18n.getString(_TL_('We cannot detect {{tagName}}{{/tagName}} at the URL you provided above. If you just installed your UET tag, it may take a few minutes until it is active. You can select {{bold}}Skip for now{{/bold}} and come back to this step later.'))}
        />
        {
      }
        <LearnMoreLink
          linkText={i18n.getString(_TL_('Learn how to verify if my UET tag is installed'))}
          topic="app54548"
        />
      </p>),
  }), [i18n, tagNameModel]);

  const cleanUp = useCallback(() => {
    // clear listeners, timeout and interval
    if (currentAbortController.current) {
      currentAbortController.current.abort();
    }
    clearTimeout(connectionTimeout.current);
    clearInterval(currentTimer.current);
    clearInterval(closeTimer.current);
  }, []);

  // cleanup on unmount
  useEffect(() => cleanUp, [cleanUp]);


  useEffect(() => {
    let notificationId;
    if (!_.isEmpty(notificationError)) {
      if (notificationError) {
        notificationId = notificationApi.showWarning(notificationError);
      }
    }
    return () => notificationId && notificationApi.dismiss(notificationId);
  }, [notificationError]);

  const listenToEvents = useCallback((messageCallback) => {
    const abortController = new AbortController();
    currentAbortController.current = abortController;
    const addEventListener = useMockListener ? window.mockAddEventListener : window.addEventListener;
    addEventListener('message', (event) => {
      if (event.data) {
        messageCallback({ event, abortController });
      }
    }, { signal: abortController.signal });
  }, [useMockListener]);

  const initConnection = useCallback(() => {
    receivedACK.current = false;
    const urlObject = getUrlObjectWithDebugParameter(websiteUrl);
    const websiteUrlToOpen = urlObject.href;

    const tab = window.open(websiteUrlToOpen);
    if (!tab) {
      logActivity.trace(`[ClarityEventSetup] Failed to open website: ${websiteUrlToOpen} with tag Id: ${tagId}`);
      setErrorMsg(errorMessages.NewTabError);
      setCurrentStage(STAGES.NOTCONNECTED);
      return;
    }
    sessionId.current = generateGuid();
    setErrorMsg('');
    const { origin } = urlObject;
    setCurrentTab(tab);
    currentTimer.current = setInterval(() => {
      tab.postMessage({ type: MESSAGES.INIT, params: { ...messageParams, SessionId: sessionId.current } }, { targetOrigin: origin });
    }, 200);

    // wait for ACK
    listenToEvents(({ event, abortController }) => {
      if (event.data.type === MESSAGES.ACK) {
        logActivity.trace(`[ClarityEventSetup] Received ACK from website: ${websiteUrlToOpen}, session Id: ${sessionId.current}`);
        abortController.abort();
        setCurrentStage(STAGES.CONNECTED);
        receivedACK.current = true;
        clearInterval(currentTimer.current);
      }
    });

    closeTimer.current = setInterval(() => {
      if (tab.closed) {
        logActivity.trace(`[ClarityEventSetup] Website: ${websiteUrlToOpen} was closed with session Id: ${sessionId.current}`);
        setCurrentStage(STAGES.NOTCONNECTED);
        cleanUp();
      }
    }, 200);

    connectionTimeout.current = setTimeout(() => {
      if (!receivedACK.current) {
        setCurrentStage(STAGES.NOTCONNECTED);
        cleanUp();
        setErrorMsg(errorMessages.CannotDetectTagError);
        logActivity.trace(`[ClarityEventSetup] Failed to receive ACK from website: ${websiteUrlToOpen} with tag Id: ${tagId}, session Id: ${sessionId.current}`);
      }
    }, 20000);
    setCurrentStage(STAGES.WAITINGFORACK);
  }, [websiteUrl, listenToEvents, setCurrentStage, logActivity, tagId, errorMessages.NewTabError, errorMessages.CannotDetectTagError, messageParams, cleanUp]);


  const reinitConnection = useCallback(() => {
    currentTab.postMessage({ type: MESSAGES.ACK_REINIT, params: { ...messageParams, SessionId: sessionId.current } }, { targetOrigin: websiteUrl });
  }, [currentTab, websiteUrl, messageParams]);


  const getRevenueEvents = (RevenueDefinition, CurrencyCode) => {
    if (!RevenueDefinition) {
      return [];
    }

    return [
      {
        Name: 'value',
        Value: JSON.stringify(RevenueDefinition),
        Source: 'clarity',
        DataType: 'number',
      },
      {
        Name: 'currency',
        Value: CurrencyCode,
        Source: 'preDefined',
        DataType: 'string',
      },
    ];
  };


  const saveClarityEventInfo = useCallback(({ EventDefinition, CurrencyCode }) => {
    setShouldBlockerLoading(true);
    const { ButtonDefinition, RevenueDefinition } = EventDefinition;
    const revenueEvents = getRevenueEvents(RevenueDefinition, CurrencyCode);
    let Trigger = null;
    if (goalContext.Type === 'EventGoal') {
      Trigger = {
        EventType: 'click',
        EventTarget: ButtonDefinition,
      };
    } else {
      // destination URL
      Trigger = {
        EventType: 'pageView',
      };
    }

    const clarityEventInfo = {
      UetTagId: tagId,
      SetupUrl: websiteUrl,
      Trigger,
      Status: 'Active',
      Data: [
        {
          Name: 'event_action',
          Value: goalAction || goalContext.Action,
          Source: 'preDefined',
          DataType: 'string',
        },
        ...revenueEvents,
      ],
    };

    // handle edit case
    if (clarityEventInfoId.current) {
      clarityEventInfo.Id = clarityEventInfoId.current;
    }

    postClarityEventInfo(
      wizardContext.cid.toString(), wizardContext.aid.toString(),
      goalContext.Id, Token, clarityEventInfo
    ).then(() => {
      wizardContext.goToNext();
    });
  }, [Token, goalAction, goalContext.Action, goalContext.Id, goalContext.Type, postClarityEventInfo, setShouldBlockerLoading, tagId, websiteUrl, wizardContext]);

  const extractButtonDefinition = useCallback((clarityEventInfo) => {
    if (clarityEventInfo.Trigger.EventType === 'click') {
      return clarityEventInfo.Trigger.EventTarget;
    }
    return null;
  }, []);

  const extractRevenueDefinition = useCallback((clarityEventInfo) => {
    const event = _.find(clarityEventInfo.Data, e => e.Name === 'value');
    if (event) {
      return event.Value;
    }
    return null;
  }, []);

  const retrieveClarityEventInfo = useCallback(() => {
    isRetrievingClarityEvent.current = true;
    getClarityEventInfo(
      wizardContext.cid.toString(), wizardContext.aid.toString(),
      goalContext.Id, Token
    ).then((clarityEventInfo) => {
      if (!clarityEventInfo || !clarityEventInfo.Id) {
        return;
      }
      const RevenueDefinition = extractRevenueDefinition(clarityEventInfo);
      const ButtonDefinition = extractButtonDefinition(clarityEventInfo);
      if (RevenueDefinition || ButtonDefinition) {
        setPageEvent({ RevenueDefinition, ButtonDefinition });
        setCurrentStage(STAGES.TRACKINGEVENT);
        setClarityEventObjectExists(true);
      }
      clarityEventInfoId.current = clarityEventInfo.Id;
      setWebsiteUrl(clarityEventInfo.SetupUrl);
    })
      .finally(() => {
        setShouldBlockerLoading(false);
      });
  }, [Token, extractButtonDefinition, extractRevenueDefinition, getClarityEventInfo, goalContext.Id, setClarityEventObjectExists, setShouldBlockerLoading, wizardContext.aid, wizardContext.cid]);

  const listenForEventData = useCallback(() => {
    listenToEvents(({ event }) => {
      if (event.data.type === MESSAGES.SAVE_EVENT) {
        logActivity.trace(`[ClarityEventSetup] Received event data from: ${websiteUrl} with tag Id: ${tagId}, session Id: ${sessionId.current}`);
        const { origin } = new URL(websiteUrl);
        currentTab.postMessage({ type: MESSAGES.ACK_SAVE_EVENT }, { targetOrigin: origin });
        cleanUp();
        saveClarityEventInfo(event.data.params);
      } else if (event.data.type === MESSAGES.REINIT) {
        reinitConnection();
      } else if (event.data.type === MESSAGES.END_SESSION) {
        setCurrentStage(STAGES.NOTCONNECTED);
        cleanUp();
      }
    });
    setCurrentStage(STAGES.SYNCINGEVENTS);
  }, [cleanUp, currentTab, listenToEvents, logActivity, reinitConnection, saveClarityEventInfo, setCurrentStage, tagId, websiteUrl]);

  if (!isRetrievingClarityEvent.current) {
    retrieveClarityEventInfo();
  }

  switch (currentStage) {
    case STAGES.NOTCONNECTED:
      break;
    case STAGES.STARTINIT:
      initConnection();
      break;
    case STAGES.WAITINGFORACK:
      break;
    case STAGES.CONNECTED:
      listenForEventData();
      break;
    case STAGES.SYNCINGEVENTS:
      break;
    default:
  }
  const startEventSetup = useCallback(() => {
    logActivity.trace(`[ClarityEventSetup] Connecting to ${websiteUrl} with tag Id: ${tagId}`);
    if (!validateUrl(websiteUrl)) {
      logActivity.trace(`[ClarityEventSetup] Invalid website URL: ${websiteUrl}`);
      setErrorMsg(errorMessages.InvalidUrl);
      return;
    }
    setCurrentStage(STAGES.STARTINIT);
  }, [errorMessages.InvalidUrl, logActivity, setCurrentStage, tagId, websiteUrl]);

  const disconnectEventSetup = useCallback(() => {
    currentTab.close();
    setCurrentStage(STAGES.NOTCONNECTED);
    cleanUp();
    logActivity.trace(`[ClarityEventSetup] ${websiteUrl} was disconnected with session Id: ${sessionId.current}`);
  }, [cleanUp, currentTab, logActivity, setCurrentStage, websiteUrl]);

  const handleWebsiteUrlChange = useCallback((websiteUrlChangeEvent, url) => {
    setWebsiteUrl(url);
  }, []);

  const handleRedoTracking = useCallback(() => {
    logActivity.trace('[ClarityEventSetup] Rest tracking called');
    setCurrentStage(STAGES.NOTCONNECTED);
    setPageEvent(null);
    setClarityEventObjectExists(false);
  }, [logActivity, setClarityEventObjectExists]);

  const switchToManual = () => {
    logActivity.trace(`[ClarityEventSetup] Switch to manual clicked for tag id: ${tagId}`);
    if (clarityEventInfoId) {
      // delete clarity association
      const payload = processPayload(wizardContext.goalContext, wizardContext.permissions, true);
      saveGoal(payload, wizardContext).then(() => {
        goToManualSetupPage(payload);
      })
        .catch((response) => {
          setNotificationError(getErrorMessage(response, i18n));
        });
    } else {
      goToManualSetupPage();
    }
  };

  const redoTrackingView = useCallback(() => {
    const buttonName = (pageEvent.ButtonDefinition && pageEvent.ButtonDefinition.t) || '';
    const eventTracking = (pageEvent.ButtonDefinition && <Badge className="event-tracking-badge" text={i18n.getString(_TL_('Event Tracking'))} secondaryText={buttonName} />);
    const revenueTracking = (pageEvent.RevenueDefinition && <Badge className="revenue-tracking-badge" text={i18n.getString(_TL_('Revenue Tracking'))} />);
    return (
      <div className={classes.redoTrackingViewContainer}>
        <b>Your event tracking is all set up!</b>
        <p className={classNames(classes.websiteUrlText, 'website-url-text')}>{websiteUrl}</p>
        <div className={classes.badgesContainer}>
          {eventTracking}
          {revenueTracking}
        </div>

      </div>
    );
  }, [classes, i18n, pageEvent, websiteUrl]);

  const eventTrackingView = useCallback(() => {
    if (currentStage === STAGES.TRACKINGEVENT) {
      return (
        <React.Fragment>
          {redoTrackingView()}
          <DefaultButton text={i18n.getString(_TL_('Redo tracking'))} onClick={handleRedoTracking} className={classNames(classes.eventTrackingButton, 'redo-tracking-button')} />
        </React.Fragment>
      );
    } else if (currentStage === STAGES.NOTCONNECTED) {
      return (
        <React.Fragment>
          <TextField onChange={handleWebsiteUrlChange} value={websiteUrl} className={classNames(classes.websiteURLTextField, 'website-URL-text-field')} />
          <DefaultButton text={i18n.getString(_TL_('Connect'))} onClick={startEventSetup} className={classNames(classes.eventTrackingButton, 'event-tracking-connect-button')} />
        </React.Fragment>
      );
    }
    return (
      <React.Fragment>
        <TextField disabled value={websiteUrl} className={classNames(classes.websiteURLTextField, 'website-URL-text-field')} />
        <DefaultButton text={i18n.getString(_TL_('Disconnect'))} onClick={disconnectEventSetup} className={classNames(classes.eventTrackingButton, 'event-tracking-disconnect-button')} />
      </React.Fragment>
    );
  }, [classes, currentStage, disconnectEventSetup, handleRedoTracking, handleWebsiteUrlChange, i18n, redoTrackingView, startEventSetup, websiteUrl]);

  return (
    <div className={classNames(classes.clarityEventSetup, 'clarity-event-setup')}>
      <div className={classes.eventTrackingContainer}>
        <div>
          <div>
            <div className={classes.nonAccordionTitle}>
              <span className="title">
                {i18n.getString(_TL_('Code-free, powered by Microsoft Clarity'))}
                <LearnMore i18n={i18n} topicId="app54820" ariaLabel={i18n.getString(_TL_('Code-free, powered by Microsoft Clarity'))} />
              </span>
            </div>
            <p>
              {i18n.getString(_TL_('Open your website below and select the area you\'d like to track as your conversion event. Your conversion event will start sending data to Microsoft Advertising—no code needed!'))}
            </p>
          </div>
          {currentStage !== STAGES.TRACKINGEVENT &&
          <b>
            {i18n.getString(_TL_('Which webpage you want to track?'))}
          </b>
    }
          <div className={classes.eventTrackingViewContainer}>
            {eventTrackingView()}
          </div>
        </div>
        <div>
          <ClarityEventSetupSvg />
        </div>
      </div>
      {errorMsg &&
      <div className="alert alert-error">
        <span className="alert-image" />
        <span className="error-message">
          {errorMsg}
        </span>
      </div>}
      <div>
        {i18n.getString(_TL_('Want to set event tracking in a different way?'))}
        <button className="clickable btn-link switch-to-manual" onClick={switchToManual}>{i18n.getString(_TL_('Switch to manual installation'))}</button>
      </div>
      {wizardContext.deleteClarityEvent &&
      <div className="alert alert-warning">
        <span className="alert-image" />
        <span>
          {i18n.getString(_TL_('Since you\'ve made updates to your goal type, you\'ll need to redo your event tracking. Open your website above to continue.'))}
        </span>
      </div>}
    </div>
  );
};
UnstyledClarityEventSetup.propTypes = {
  classes: PropTypes.shape(_.mapObject(
    getStepStyles(),
    _.constant(PropTypes.string.isRequired)
  )).isRequired,
  serverContext: PropTypes.shape({
    Token: PropTypes.string.isRequired,
    PageTrackingId: PropTypes.string.isRequired,
    CurrentUser: PropTypes.shape({ Id: PropTypes.string.isRequired }).isRequired,
  }).isRequired,
  scope: PropTypes.shape({
    values: PropTypes.shape({ useMockListener: PropTypes.string }).isRequired,
  }).isRequired,
  clarityTagSetupRepository: PropTypes.shape({
    getClarityEventInfo: PropTypes.func.isRequired,
    postClarityEventInfo: PropTypes.func.isRequired,
    getErrorMessage: PropTypes.func.isRequired,
    saveGoal: PropTypes.func.isRequired,
    processPayload: PropTypes.func.isRequired,
  }).isRequired,
  wizardContext: PropTypes.shape({
    cid: PropTypes.string.isRequired,
    aid: PropTypes.string.isRequired,
    goBack: PropTypes.func.isRequired,
    goToNext: PropTypes.func.isRequired,
    cancel: PropTypes.func.isRequired,
    deleteClarityEvent: PropTypes.bool.isRequired,
    goalContext: PropTypes.shape({
      tags: PropTypes.arrayOf(PropTypes.shape({ Id: PropTypes.string })).isRequired,
      Tag: PropTypes.shape({ Id: PropTypes.string }).isRequired,
      Name: PropTypes.string,
      Type: PropTypes.string,
      ValueType: PropTypes.string,
      Operator: PropTypes.string,
      UrlString: PropTypes.string,
      CurrencyCode: PropTypes.string,
      Action: PropTypes.string,
      Id: PropTypes.string,
      GoalValueType: PropTypes.string,
    }),
    permissions: PropTypes.shape({}).isRequired,
    logActivity: PropTypes.func.isRequired,
  }).isRequired,
  goToManualSetupPage: PropTypes.func.isRequired,
  setClarityEventObjectExists: PropTypes.func.isRequired,
  setShouldBlockerLoading: PropTypes.func.isRequired,
  goalAction: PropTypes.string,
};

UnstyledClarityEventSetup.defaultProps = {
  goalAction: undefined,
};

export const ClarityEventSetup = withPermissions(withScope(withDefaultStyles(UnstyledClarityEventSetup, styles)));
